import { ClientRect } from '@dnd-kit/core'
import { CSS, Transform } from '@dnd-kit/utilities'
import { DateTime, Interval } from 'luxon'
import { MutableRefObject } from 'react'
import { DashboardEntry } from '../api/dashboard'
import { DateRangeFormatter } from '../components/daterange-formatter'
import { Tooltip, TooltipWrap } from '../components/tooltip'
import { BASE_BORDER, BASE_COLOR } from './consts'

export function NewDragOverlay({
	width,
	left,
	interval,
	seconds,
	containerRect,
}: {
	width: number
	left: number
	interval: Interval<true>
	seconds: number
	containerRect: MutableRefObject<ClientRect | null>
}) {
	const containerWidth = containerRect?.current?.width
	const style = { width, left }

	if (!containerWidth) return null

	const offset = getOffset(seconds, left, containerWidth)
	const date = interval.start.plus({ seconds: offset })

	const times = getDropTimes({
		type: 'left',
		entry: {
			dateFrom: date,
			dateTo: date,
		},
		seconds,
		delta: width,
		containerWidth,
	})

	return (
		<Tooltip content={<DateRangeFormatter dateFrom={times[0]} dateTo={times[1]} />} isOpen>
			<TooltipWrap>
				<div
					className={`absolute opacity-50 border-box bg-gradient-to-b ${BASE_COLOR.new} border rounded-r ${BASE_BORDER.new} h-5 cursor-ew-resize`}
					style={style}
				/>
			</TooltipWrap>
		</Tooltip>
	)
}

export function HorizontalDragOverlay({
	width,
	left,
	right,
	entry,
	seconds,
	type,
	containerRect,
}: {
	width: number
	left?: number
	right?: number
	entry: DashboardEntry
	seconds: number
	type: 'left' | 'right'
	containerRect: MutableRefObject<ClientRect | null>
}) {
	const containerWidth = containerRect?.current?.width
	const style = { width, left, right }

	if (!containerWidth) return null

	let offset = 0
	if (type === 'left') {
		offset = left || width
	} else {
		offset = right ? width : -1 * width
	}

	const times = getDropTimes({
		entry: entry,
		type: type,
		seconds,
		delta: offset,
		containerWidth,
	})

	const colorKey = entry.status

	return (
		<Tooltip content={<DateRangeFormatter dateFrom={times[0]} dateTo={times[1]} />} isOpen>
			<TooltipWrap>
				<div
					className={`absolute opacity-50 border-box bg-gradient-to-b ${BASE_COLOR[colorKey]} border rounded-r ${BASE_BORDER[colorKey]} h-5 cursor-ew-resize`}
					style={style}
				/>
			</TooltipWrap>
		</Tooltip>
	)
}

export function VerticalDragOverlay({
	entry,
	top,
	left,
	width,
	seconds,
	transform,
	containerRect,
}: {
	entry: DashboardEntry
	top: number
	left: string
	width: string
	seconds: number
	transform: Transform | null
	containerRect: MutableRefObject<ClientRect | null>
}) {
	if (!transform || !containerRect.current) return null

	const offset = getOffset(seconds, transform.x, containerRect.current.width)
	const dateFrom = entry.dateFrom.plus({ seconds: offset })
	const dateTo = entry.dateTo.plus({ seconds: offset })
	const colorKey = entry.status

	return (
		<Tooltip content={<DateRangeFormatter dateFrom={dateFrom} dateTo={dateTo} />} isOpen>
			<TooltipWrap>
				<div
					tabIndex={0}
					className={`absolute border-box bg-gradient-to-b ${BASE_COLOR[colorKey]} border rounded ${BASE_BORDER[colorKey]} h-5 flex items-center justify-between`}
					style={{
						top,
						left,
						width,
						transform: CSS.Translate.toString(transform),
					}}
				></div>
			</TooltipWrap>
		</Tooltip>
	)
}

function getOffset(seconds: number, delta: number, containerWidth: number) {
	const offset = (seconds * delta) / containerWidth
	return Math.round(offset / 3600) * 3600
}

function getDropTimes({
	entry,
	type,
	seconds,
	delta,
	containerWidth,
}: {
	delta: number
	entry: Pick<DashboardEntry, 'dateFrom' | 'dateTo'>
	seconds: number
	type: 'left' | 'right'
	containerWidth: number
}) {
	const offset = getOffset(seconds, delta, containerWidth)

	if (offset === 0) {
		return [entry.dateFrom, entry.dateTo]
	}

	const times: DateTime[] = []

	if (type === 'left') {
		times.push(entry.dateTo)
		times.push(entry.dateFrom.plus({ seconds: offset }))
	} else if (type === 'right') {
		times.push(entry.dateFrom)
		times.push(entry.dateTo.plus({ seconds: offset }))
	}

	times.sort((a, b) => a.valueOf() - b.valueOf())

	return times
}
